import React from 'react';

import { LegacyTable, ReportSection } from '../ReportSection';

import {
  avgTimeToFirstQuote,
  avgVersionGap,
  avgVersions,
  avgVersionsWon,
  contactabilityToActive,
  days,
  inr,
  lostTrustability,
  num,
  pct,
  queryToActive,
  ratio,
  wonCount,
} from './band-helpers';

import type { BandProps } from './band-props';

import { LeadsBusinessStrip } from '@/features/leads/components/LeadsBusinessStrip';

/**
 * The former "Overview" band, now split so its two blocks can sit at different
 * positions in the client dashboard flow (pure relocation - no data/logic
 * change). `variant="target"` renders only the Targets strip (position 1);
 * `variant="business"` renders only the Business Number table (position 7,
 * inside the Performance group). Both variants read the SAME props.
 */
export const OverviewBand = ({
  variant,
  embedded,
  report,
  ownerId,
  ownerOptions,
  agentEntries,
  agentCell,
  expandAllAction,
  loading,
  canSeeTrustability,
}: BandProps & { variant: 'target' | 'business' }): React.JSX.Element => {
  if (variant === 'target') {
    return (
      <div className="space-y-4 pt-2">
        {/* Row 2: agent/team targets - the SAME strip (data + design + RBAC) as
            the Lead Inbox. Current-month only for now (Target is a live current
            quota; past-month reporting is a separate cleanup). Hidden when
            embedded under the inbox, since the inbox already renders it above. */}
        {!embedded && (
          <LeadsBusinessStrip
            ownerId={ownerId}
            ownerName={ownerOptions.find((o) => o.id === ownerId)?.name ?? null}
            ownerRole={
              (report?.rows.find((r) => r.agentId === ownerId)?.role as
                | 'SALES_AGENT'
                | 'SALES_LEAD'
                | null) ?? null
            }
          />
        )}
      </div>
    );
  }
  return (
    <div className="space-y-4 pt-2">
      {/* Agent by Performance (Current Month) - the client's two focused
          per-agent performance tables. Same per-agent metrics already shown as
          scoped cards in the Performance band, laid out agent-by-agent above the
          wider Business Number table. */}
      <ReportSection title="Agent by Performance (Conversion)" actions={expandAllAction}>
        <LegacyTable
          loading={loading || !report}
          headers={[
            'Agent',
            'Lead to Contactability',
            'Lead to Active',
            'Contactability to Active',
            'Avg Days to Convert',
            'No. of Files',
            'Avg Ticket Value',
          ]}
          rows={agentEntries.map((entry) => {
            const row = entry.row;
            const won = wonCount(report, row);
            return [
              { node: agentCell(entry), sort: row.agentName },
              pct(row.contactabilityPct),
              queryToActive(row),
              contactabilityToActive(row),
              days(row.avgConversionDays),
              num(won),
              won > 0 ? inr(row.wonTtvAmount / won) : inr(0),
            ];
          })}
        />
      </ReportSection>

      <ReportSection title="Agent by Performance (Itinerary & TTV)" actions={expandAllAction}>
        <LegacyTable
          loading={loading || !report}
          headers={[
            'Agent',
            'Active / Itinerary',
            'Avg Itinerary Versions',
            'Avg Versions (Converted)',
            ...(canSeeTrustability ? ['Lost Trustability'] : []),
            'Potential TTV (All Leads)',
            'Received',
          ]}
          rows={agentEntries.map((entry) => {
            const row = entry.row;
            return [
              { node: agentCell(entry), sort: row.agentName },
              `${num(row.activeItinPublished)}/${num(row.activeItinTotal)}`,
              avgVersions(row),
              avgVersionsWon(row),
              ...(canSeeTrustability ? [lostTrustability(row)] : []),
              inr(row.potentialTtvAll),
              inr(row.receivedTtvAll),
            ];
          })}
        />
      </ReportSection>

      {/* De-duplicated: the pool / performance / large-leads / period-of-loss
          KPI tiles now live in the consolidated pool + performance bands. The
          Business Number report table lives in the Performance group. The
          Active w/ Itinerary drill moved to the Performance band. */}
      <ReportSection title="Business Number" actions={expandAllAction}>
        <LegacyTable
          loading={loading || !report}
          headers={[
            'Agent',
            'Avg. Period of Loss',
            'Avg Loss (NQ)',
            'Avg Loss (Active)',
            'Avg Loss (NCL)',
            'Avg Days to Convert',
            'ATV',
            'Target Amount',
            'QPA MTD',
            'AQPA',
            'Bookings',
            'MTD Conversion %',
            'TTV',
            'Shortfall',
            'Since Last Booking',
            'Avg Versions',
            'Avg Versions (Won)',
            'Time to First Quote',
            'Time Between Versions',
            ...(canSeeTrustability ? ['Lost Trustability'] : []),
          ]}
          rows={agentEntries.map((entry) => {
            const row = entry.row;
            const won = wonCount(report, row);
            // TTV = pre-tax selling value of won deals (before GST/TCS), not the
            // whole agreed saleAmount. Drives ATV and Shortfall too.
            const ttv = row.wonTtvAmount;
            const targetAmount = row.target?.amount ?? 0;
            return [
              { node: agentCell(entry), sort: row.agentName },
              row.avgLossDays !== null ? `${row.avgLossDays} days` : '-', // Avg. period of loss
              row.avgLossDaysNq !== null ? `${row.avgLossDaysNq} days` : '-', // From NQ
              row.avgLossDaysActive !== null ? `${row.avgLossDaysActive} days` : '-', // From Active
              row.avgLossDaysNonContact !== null ? `${row.avgLossDaysNonContact} days` : '-', // Non-contactable
              row.avgConversionDays !== null ? `${row.avgConversionDays} days` : '-', // Days to convert
              won > 0 ? inr(ttv / won) : inr(0), // ATV = TTV / bookings
              inr(targetAmount),
              row.totalLeads, // QPA MTD
              (row.totalLeads / 25).toFixed(2),
              won, // Bookings
              ratio(won, row.totalLeads), // MTD Conversion %
              inr(ttv), // TTV
              inr(Math.max(0, targetAmount - ttv)), // Shortfall
              row.daysSinceLastBooking !== null ? `${row.daysSinceLastBooking} days` : '-', // Since last booking
              avgVersions(row), // Avg itinerary versions per lead
              avgVersionsWon(row), // Avg versions per won lead
              avgTimeToFirstQuote(row), // Lead active -> first customer-facing quote
              avgVersionGap(row), // Avg time between consecutive versions sent
              ...(canSeeTrustability ? [lostTrustability(row)] : []), // Lost approval acceptance % (management-only)
            ];
          })}
        />
      </ReportSection>
    </div>
  );
};
